import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Calendar, Plus, Trash2, Palmtree, Sun, CalendarX2, Info, CalendarDays, Building2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { companyApi } from '../../../lib/api';
import { format, parseISO, isAfter, isBefore, addYears } from 'date-fns';
import { fr } from 'date-fns/locale';
import useFabAction from '../../../hooks/useFabAction';

const CLOSURE_TYPES = [
  { value: 'holiday', label: 'Jour férié', icon: CalendarDays, color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'vacation', label: 'Vacances', icon: Palmtree, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'other', label: 'Autre fermeture', icon: Building2, color: 'bg-blue-100 text-blue-700 border-blue-200' },
];

// French holidays (recurring each year)
const FRENCH_HOLIDAYS = [
  { name: 'Jour de l\'An', month: 1, day: 1 },
  { name: 'Fête du Travail', month: 5, day: 1 },
  { name: 'Victoire 1945', month: 5, day: 8 },
  { name: 'Fête Nationale', month: 7, day: 14 },
  { name: 'Assomption', month: 8, day: 15 },
  { name: 'Toussaint', month: 11, day: 1 },
  { name: 'Armistice', month: 11, day: 11 },
  { name: 'Noël', month: 12, day: 25 },
];

export default function ClosuresPage() {
  const [closures, setClosures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newClosure, setNewClosure] = useState({
    date: '',
    end_date: '',
    reason: '',
    closure_type: 'vacation',
    is_recurring: false,
    blocks_public_widgets: true  });

  const fetchClosures = async () => {
    try {
      const res = await companyApi.getClosures();
      setClosures(res.data);
    } catch (error) {
      console.error('Error fetching closures:', error);
      toast.error('Erreur lors du chargement des fermetures');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchClosures(); }, []);

  useFabAction({ new: () => setIsDialogOpen(true) });

  const resetForm = () => {
    setNewClosure({
      date: '',
      end_date: '',
      reason: '',
      closure_type: 'vacation',
      is_recurring: false,
      blocks_public_widgets: true
    });
  };

  const handleCreate = async () => {
    if (!newClosure.date) {
      toast.error('Veuillez sélectionner une date de début');
      return;
    }
    if (!newClosure.reason) {
      toast.error('Veuillez ajouter un motif');
      return;
    }

    setIsSubmitting(true);
    try {
      await companyApi.createClosure({
        date: newClosure.date,
        end_date: newClosure.end_date || null,
        reason: newClosure.reason,
        closure_type: newClosure.closure_type,
        is_recurring: newClosure.is_recurring,
        blocks_public_widgets: newClosure.blocks_public_widgets
      });
      toast.success('Fermeture ajoutée');
      setIsDialogOpen(false);
      resetForm();
      fetchClosures();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette fermeture ?')) return;
    try {
      await companyApi.deleteClosure(id);
      toast.success('Fermeture supprimée');
      fetchClosures();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const addQuickHoliday = async (holiday) => {
    const year = new Date().getFullYear();
    const date = `${year}-${String(holiday.month).padStart(2, '0')}-${String(holiday.day).padStart(2, '0')}`;
    
    // Check if already exists
    const exists = closures.some(c => c.date === date || (c.reason === holiday.name && c.is_recurring));
    if (exists) {
      toast.error('Ce jour férié est déjà ajouté');
      return;
    }

    try {
      await companyApi.createClosure({
        date,
        reason: holiday.name,
        closure_type: 'holiday',
        is_recurring: true,
        blocks_public_widgets: true
      });
      toast.success(`${holiday.name} ajouté`);
      fetchClosures();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const getTypeConfig = (type) => CLOSURE_TYPES.find(t => t.value === type) || CLOSURE_TYPES[2];

  const formatDateRange = (start, end) => {
    const startDate = parseISO(start);
    if (!end || start === end) {
      return format(startDate, 'd MMMM yyyy', { locale: fr });
    }
    const endDate = parseISO(end);
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${format(startDate, 'd', { locale: fr })} - ${format(endDate, 'd MMMM yyyy', { locale: fr })}`;
    }
    return `${format(startDate, 'd MMM', { locale: fr })} - ${format(endDate, 'd MMM yyyy', { locale: fr })}`;
  };

  // Sort closures: future first, then past
  const sortedClosures = [...closures].sort((a, b) => {
    const now = new Date();
    const dateA = parseISO(a.date);
    const dateB = parseISO(b.date);
    const aIsPast = isBefore(dateA, now);
    const bIsPast = isBefore(dateB, now);
    if (aIsPast !== bIsPast) return aIsPast ? 1 : -1;
    return dateA - dateB;
  });

  const upcomingClosures = sortedClosures.filter(c => !isBefore(parseISO(c.end_date || c.date), new Date()));
  const pastClosures = sortedClosures.filter(c => isBefore(parseISO(c.end_date || c.date), new Date()));

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded w-1/3 animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Fermetures Entreprise</h1>
          <p className="text-muted-foreground mt-1">Gérez les jours fériés et périodes de fermeture</p>
        </div>
        <Button className="rounded-full" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une fermeture
        </Button>
      </div>

      {/* Info banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Comment ça marche ?</p>
            <ul className="space-y-1 text-blue-700">
              <li>• Les dates de fermeture <strong>bloquent automatiquement</strong> les réservations sur vos widgets publics.</li>
              <li>• Vous pouvez toujours créer manuellement des réservations pour ces jours dans votre planning.</li>
              <li>• Les jours fériés récurrents s'appliquent automatiquement chaque année.</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Quick add French holidays */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <CalendarDays className="w-4 h-4" />
            Ajouter rapidement les jours fériés
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {FRENCH_HOLIDAYS.map(holiday => (
              <Button
                key={holiday.name}
                variant="outline"
                size="sm"
                onClick={() => addQuickHoliday(holiday)}
                className="text-xs"
              >
                {holiday.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Closures list */}
      <div className="space-y-4">
        {upcomingClosures.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium">Fermetures à venir</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {upcomingClosures.map(closure => {
                const typeConfig = getTypeConfig(closure.closure_type);
                const TypeIcon = typeConfig.icon;
                return (
                  <div 
                    key={closure.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeConfig.color}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium">{closure.reason}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{formatDateRange(closure.date, closure.end_date)}</span>
                          {closure.is_recurring && (
                            <Badge variant="outline" className="text-[10px]">Récurrent</Badge>
                          )}
                          {!closure.blocks_public_widgets && (
                            <Badge variant="outline" className="text-green-600 border-green-200 text-[10px]">
                              Résa. autorisées
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => handleDelete(closure.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {pastClosures.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium text-muted-foreground">Fermetures passées</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {pastClosures.slice(0, 5).map(closure => {
                const typeConfig = getTypeConfig(closure.closure_type);
                const TypeIcon = typeConfig.icon;
                return (
                  <div 
                    key={closure.id}
                    className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-dashed opacity-60"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeConfig.color}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium">{closure.reason}</p>
                        <span className="text-sm text-muted-foreground">
                          {formatDateRange(closure.date, closure.end_date)}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => handleDelete(closure.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {closures.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <CalendarX2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">Aucune fermeture planifiée</p>
              <Button className="mt-4 rounded-full" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une fermeture
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Nouvelle fermeture</DialogTitle>
            <DialogDescription>
              Ajoutez une période pendant laquelle vous ne serez pas disponible
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type de fermeture</Label>
              <Select 
                value={newClosure.closure_type} 
                onValueChange={(v) => setNewClosure({...newClosure, closure_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLOSURE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Motif *</Label>
              <Input
                value={newClosure.reason}
                onChange={(e) => setNewClosure({...newClosure, reason: e.target.value})}
                placeholder="Ex: Vacances d'été, Noël..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début *</Label>
                <Input
                  type="date"
                  value={newClosure.date}
                  onChange={(e) => setNewClosure({...newClosure, date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={newClosure.end_date}
                  onChange={(e) => setNewClosure({...newClosure, end_date: e.target.value})}
                  min={newClosure.date}
                />
                <p className="text-[10px] text-muted-foreground">Laisser vide pour un jour unique</p>
              </div>
            </div>

            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Récurrent chaque année</Label>
                  <p className="text-xs text-muted-foreground">Se répète automatiquement</p>
                </div>
                <Switch
                  checked={newClosure.is_recurring}
                  onCheckedChange={(checked) => setNewClosure({...newClosure, is_recurring: checked})}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Bloquer les réservations</Label>
                  <p className="text-xs text-muted-foreground">Empêche les clients de réserver</p>
                </div>
                <Switch
                  checked={newClosure.blocks_public_widgets}
                  onCheckedChange={(checked) => setNewClosure({...newClosure, blocks_public_widgets: checked})}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={isSubmitting}>
              {isSubmitting ? 'Ajout...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
